import React, { useEffect } from 'react';
import { Step } from '../ProductCard';
import TrayContainer from './Container';
import useVariantAvailability from '~/hooks/components/use-variant-availability';
import CurrentTrayStep from './CurrentTrayStep';

export type SizesWithStock = Queries.OptionValuesFragmentFragment & { isAvailable: boolean | null };

export type SharedProps = {
  product: Queries.ProductCardFragmentFragment;
  selectedVariant: Queries.VariantDetailsFragmentFragment | null;
  selectedColor: Queries.OptionValuesFragmentFragment | null;
  handleSizeClick: (optionValue: Queries.OptionValuesFragmentFragment) => void;
  selectedSize: Queries.OptionValuesFragmentFragment | null;
  currentStep: Step;
  setCurrentStep: React.Dispatch<React.SetStateAction<Step>>;
  setSelectedSize: React.Dispatch<React.SetStateAction<Queries.OptionValuesFragmentFragment | null>>;
};

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  product: Queries.ProductCardFragmentFragment;
  handleSizeClick: (optionValue: Queries.OptionValuesFragmentFragment) => void;
  selectedSize: Queries.OptionValuesFragmentFragment | null;
  selectedColor: Queries.OptionValuesFragmentFragment | null;
  selectedVariant: Queries.VariantDetailsFragmentFragment | null;
  currentStep: Step;
  setCurrentStep: React.Dispatch<React.SetStateAction<Step>>;
  setSelectedSize: React.Dispatch<React.SetStateAction<Queries.OptionValuesFragmentFragment | null>>;
  setSelectedColor: React.Dispatch<React.SetStateAction<Queries.OptionValuesFragmentFragment | null>>;
  setVariantImage: React.Dispatch<React.SetStateAction<Queries.VariantDetailsFragmentFragment['mainImage'] | null>>;
} & SharedProps;

const QuickshopTray = ({
  isOpen,
  setIsOpen,
  product,
  handleSizeClick,
  selectedSize,
  selectedVariant,
  selectedColor,
  setCurrentStep,
  currentStep,
  setSelectedSize,
  setSelectedColor,
  setVariantImage,
}: Props) => {
  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep('variant');
    setSelectedSize(null);
  };

  useEffect(() => {
    if (isOpen && !selectedColor) {
      const firstVariant = product.variants?.[0];
      const firstColor = firstVariant?.optionValues?.find((optionValue) => /color/i.test(optionValue?.slug ?? ''));
      const variantImage = firstVariant?.mainImage;
      if (!firstColor || !variantImage) return;
      setSelectedColor(firstColor);
      setVariantImage(variantImage);
    }
  }, [isOpen, product, selectedColor, setSelectedColor, setVariantImage]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (currentStep === 'added-to-cart') {
      timeout = setTimeout(() => {
        setIsOpen(false);
      }, 4000);
    }
    return () => timeout && clearTimeout(timeout);
  }, [currentStep, setIsOpen]);

  const { data: variantStock, isLoading } = useVariantAvailability({
    sku: selectedVariant?.sku ?? '',
    shouldFetch: Boolean(selectedVariant?.sku),
  });

  useEffect(() => {
    if (!isLoading && variantStock === false) {
      setCurrentStep('out-of-stock');
    }
  }, [variantStock, setCurrentStep, isLoading]);

  return (
    <TrayContainer
      product={product}
      isOpen={isOpen}
      currentStep={currentStep}
      handleClose={handleClose}
      selectedColor={selectedColor}
      selectedVariant={selectedVariant}
    >
      <CurrentTrayStep
        {...{
          product,
          selectedVariant,
          selectedColor,
          isLoading,
          variantStock,
          handleSizeClick,
          selectedSize,
          currentStep,
          setCurrentStep,
          setSelectedSize,
        }}
      />
    </TrayContainer>
  );
};

export default QuickshopTray;
